import React, { Fragment, useState } from "react";
import CommonCard from "../../common/components/CommonCard";
import { Box, CircularProgress, Divider, Grid, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from "@mui/material";
import CommonButton from "../../common/components/CommonButton";
import { Add, Close } from "@mui/icons-material";
import styled from "@emotion/styled";
import theme from "../../theme";
import moment from "moment";
import CommonSelect from "../../common/components/CommonSelect";
import { showNotification } from "../../common/components/Notification/ToastifyNotification";
import { setLoading } from "../../features/authSlice";
import { useDispatch, useSelector } from "react-redux";
import CommonDialog from "../../common/components/CommonDialog";
import CommonFormField from "../../common/components/CommonFormField";
import { createMileStone, deleteMileStone, updateMileStone, updateMileStoneStatus } from "../../api/mileStoneApi";
import { MentorMileStoneStatus, StudentMileStoneStatus, UserTypeId } from "../../common/const";

const Cell = styled(TableCell)(({ theme }) => ({
    fontWeight: 600,
    padding: 5,
    textAlign: 'center',
    fontSize: 13,
    borderRight: `1px solid ${theme.palette.background.secondary}`,
    background: theme.palette.secondary.main,
    color: theme.palette.primary.main
}));

const InnerCell = styled(TableCell)(({ theme }) => ({
    padding: 10,
    textAlign: 'center',
    fontSize: 14,
    height: 70,
    color: theme.palette.background.dark,
    borderRight: `1px solid ${theme.palette.background.secondary}`,
}));

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
    border: `1px solid ${theme.palette.grey[300]}`,
    borderRadius: 1,
    height: 'auto', // Let it adjust based on content
    maxHeight: '400px', // Fixed maximum height
    overflow: 'auto', // Enable scrolling
    marginTop: 10
}));

const MileStone = ({
    fromMentor,
    projectId,
    getProjectDetails,
    progress,
    getMileStones,
    mileStones
}) => {
    const dispatch = useDispatch();
    const { user } = useSelector((state) => state.auth);
    const [comment, setComment] = useState(null);
    const [open, setOpen] = useState(false);
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [errors, setErrors] = useState({});
    const [feedBackOpen, setFeedBack] = useState(false);
    const [editData, setEditData] = useState(null);
    const [formData, setFormData] = useState({
        name: '',
        due_date: '',
        status: '',
        notes: ''
    });

    const validateField = (name, value) => {
        switch (name) {
            case 'name':
                if (!value) return 'Milestone Name is required';
                return '';
            case 'due_date':
                if (!value) return 'Due Date is required';
                return '';
            case 'status':
                if (!value) return 'Initial Status is required';
                return '';
            default:
                return '';
        }
    };

    const validateForm = () => {
        const newErrors = {};
        Object.keys(formData).forEach(key => {
            const error = validateField(key, formData[key]);
            if (error) newErrors[key] = error;
        });
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
        if (errors[name]) {
            const error = validateField(name, value);
            if (!error) {
                setErrors(prev => ({ ...prev, [name]: undefined }));
            } else {
                setErrors(prev => ({ ...prev, [name]: error }));
            }
        }
    };

    const handleSubmit = async () => {
        try {
            if (!validateForm()) return;
            let response;
            if (editData?.milestone_id) {
                response = await updateMileStone(editData?.milestone_id, {
                    project_id: projectId,
                    "milestone_name": formData?.name,
                    "description": formData?.notes,
                    "due_date": new Date(formData.due_date)?.toISOString(),
                    "status": formData?.status
                });
            } else {
                response = await createMileStone({
                    project_id: projectId,
                    "milestone_name": formData?.name,
                    "description": formData?.notes,
                    "due_date": new Date(formData.due_date)?.toISOString(),
                    "status": formData?.status
                });
            }
            if (response?.success) {
                showNotification(response?.message, 'success');
                setFormData({
                    name: '',
                    due_date: '',
                    status: '',
                    notes: ''
                });
                setEditData(null);
                setOpen(false);
                dispatch(setLoading({
                    loading: false
                }));
                getMileStones();
                if (getProjectDetails) {
                    getProjectDetails();
                }
            }
        } catch (err) {
            const errorMessage = err.response?.data?.errors?.[0]?.message || err.response?.data?.message || err?.response?.data?.error;
            showNotification(errorMessage, 'error');
            dispatch(setLoading({
                loading: false
            }));
        }
    };

    return (
        <Fragment>
            <CommonDialog
                open={open}
                size="xs"
                title={
                    <Fragment>
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Typography sx={{ fontWeight: 600, fontSize: 18 }}>{editData?.milestone_id ? 'Update MileStone' : 'Create New MileStone'}</Typography>
                            <Close
                                sx={{
                                    color: theme.palette.background.dark, fontSize: 20,
                                    float: 'right',
                                    cursor: 'pointer'
                                }}
                                onClick={() => {
                                    setOpen(false);
                                    setErrors({});
                                    setFormData({
                                        name: '',
                                        due_date: '',
                                        status: '',
                                        notes: ''
                                    });
                                }}
                            />
                        </Box>
                        <Box width="80%" mb={0.5}>
                            <Typography sx={{ fontSize: 12 }} color={theme.palette.background.dark}>Enter the milestone details</Typography>
                        </Box>
                        <Divider />
                    </Fragment>
                }
                content={
                    <Grid container spacing={2} mt={0.5} mb={1}>
                        <Grid item md={12} xs={12}>
                            <CommonFormField
                                label="Milestone Name"
                                type="email"
                                name="name"
                                value={formData.name}
                                onChange={handleInputChange}
                                error={errors.name}
                                placeholder="Enter Milestone Name"
                            />
                        </Grid>
                        <Grid item md={12} xs={12}>
                            <CommonFormField
                                label="Due Date"
                                type="date"
                                name="due_date"
                                value={formData.due_date}
                                onChange={handleInputChange}
                                error={errors.due_date}
                                placeholder="Select Due Date"
                            />
                        </Grid>
                        <Grid item md={12} xs={12}>
                            <CommonSelect
                                label="Initial Status"
                                name="status"
                                value={formData.status}
                                onChange={handleInputChange}
                                placeholder="Completed / Current"
                                error={errors.status}
                                options={StudentMileStoneStatus}
                            />
                        </Grid>
                        <Grid item md={12} xs={12}>
                            <CommonFormField
                                label="Description"
                                type="email"
                                name="notes"
                                value={formData.notes}
                                onChange={handleInputChange}
                                placeholder="Milestone description"
                                multiline
                            />
                        </Grid>
                    </Grid>
                }
                actionButtons={(
                    <CommonButton
                        label={editData?.milestone_id ? "Update" : "Create"}
                        size="small"
                        background={theme.palette.secondary.main}
                        color={theme.palette.background.main}
                        fontWeight={700}
                        marginBottom="10px"
                        handleClick={() => {
                            handleSubmit();
                        }}
                    />
                )}
            />
            <CommonDialog
                open={feedBackOpen}
                size="xs"
                title={
                    <Fragment>
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Typography sx={{ fontWeight: 600, fontSize: 18 }}>Add Feedback</Typography>
                            <Close
                                sx={{
                                    color: theme.palette.background.dark, fontSize: 20,
                                    float: 'right',
                                    cursor: 'pointer'
                                }}
                                onClick={() => {
                                    setFeedBack(false);
                                    setComment(null);
                                }}
                            />
                        </Box>
                        <Divider />
                    </Fragment>
                }
                content={
                    <Grid container spacing={2} mt={0.5} mb={1}>
                        <Grid item md={12} xs={12}>
                            <CommonFormField
                                label="Comment"
                                type="email"
                                name="comment"
                                value={comment}
                                onChange={(e) => setComment(e.target.value)}
                                placeholder="e.g., The MVP scope is too broad, please refine"
                                multiline
                            />
                        </Grid>
                    </Grid>
                }
                actionButtons={(
                    <CommonButton
                        label="Save"
                        size="small"
                        background={theme.palette.secondary.main}
                        color={theme.palette.background.main}
                        fontWeight={700}
                        marginBottom="10px"
                        handleClick={async () => {
                            try {
                                dispatch(setLoading({
                                    loading: true
                                }));
                                const response = await updateMileStoneStatus(editData?.milestone_id, {
                                    status: editData.status,
                                    mentor_comment: comment
                                });
                                if (response.success) {
                                    showNotification(response.message, 'success');
                                    setFeedBack(false);
                                    setComment(null);
                                    setEditData(null);
                                    dispatch(setLoading({
                                        loading: false
                                    }))
                                }
                            } catch (err) {
                                const errorMessage = err.response?.data?.errors?.[0]?.message || err.response?.data?.message || err?.response?.data?.error;
                                showNotification(errorMessage, 'error');
                                dispatch(setLoading({
                                    loading: false
                                }));
                            }
                        }}
                    />
                )}
            />
            <CommonDialog
                open={deleteOpen}
                size="xs"
                content={(
                    <center>
                        <Typography sx={{ fontWeight: 600, fontSize: '22px', mt: 0.5, mb: 1 }}>
                            Are you sure you want to delete Milestone?
                        </Typography>
                    </center>
                )}
                actionButtons={(
                    <Fragment>
                        <CommonButton
                            label="Yes"
                            size="small"
                            background={theme.palette.secondary.main}
                            color={theme.palette.primary.main}
                            handleClick={async () => {
                                try {
                                    const response = await deleteMileStone(editData?.milestone_id);
                                    if (response?.success) {
                                        showNotification(response?.message, 'success');
                                        setDeleteOpen(false);
                                        dispatch(setLoading({
                                            loading: false
                                        }));
                                        setEditData(null);
                                        getMileStones();
                                        getProjectDetails();
                                    }
                                } catch (err) {
                                    const errorMessage = err.response?.data?.errors?.[0]?.message || err.response?.data?.message || err?.response?.data?.error;
                                    showNotification(errorMessage, 'error');
                                    dispatch(setLoading({
                                        loading: false
                                    }));
                                }
                            }}
                        />
                        <CommonButton
                            label="No"
                            size="small"
                            background={theme.palette.background.main}
                            border={`1px solid ${theme.palette.background.light}`}
                            handleClick={() => {
                                setEditData(null);
                                setDeleteOpen(false);
                            }}
                        />
                    </Fragment>
                )}
            />
            <CommonCard
                paddingBottom="0px"
                borderRadius={2}
                content={
                    <Fragment>
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Typography fontWeight={600}>Milestones & Status</Typography>
                            {!fromMentor && (
                                <Box display="flex" gap={1} alignItems='center'>
                                    <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                                        <CircularProgress
                                            variant="determinate"
                                            value={100}
                                            size={40}
                                            thickness={6}
                                            sx={{
                                                color: '#e0e0e0', // Light gray for remaining portion
                                                position: 'absolute',
                                                '& .MuiCircularProgress-circle': {
                                                    strokeLinecap: 'round',
                                                },
                                            }}
                                        />
                                        <CircularProgress
                                            variant="determinate"
                                            value={progress}
                                            size={40}
                                            thickness={6}
                                            sx={{
                                                color: progress
                                                    ? theme.palette.secondary.default// Full green
                                                    : theme.palette.secondary.default, // Your progress color
                                                '& .MuiCircularProgress-circle': {
                                                    strokeLinecap: 'round',
                                                },
                                            }}
                                        />
                                        <Box
                                            sx={{
                                                top: 0,
                                                left: 0,
                                                bottom: 0,
                                                right: 0,
                                                position: 'absolute',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                            }}
                                        >
                                            <Typography
                                                fontSize={11}
                                                component="div"
                                                fontWeight={700}
                                                color="text.primary"
                                            >
                                                {`${progress}%`}
                                            </Typography>
                                        </Box>
                                    </Box>
                                    {user?.user_type_id === UserTypeId.Student && (
                                    <CommonButton
                                        label="Create Milestone"
                                        height="35px"
                                        startIcon={<Add />}
                                        width="180px"
                                        marginBottom="0px"
                                        handleClick={() => setOpen(true)}
                                    />)}
                                </Box>
                            )}
                        </Box>
                        <StyledTableContainer>
                            <Table stickyHeader>
                                <TableHead>
                                    <TableRow sx={{ backgroundColor: theme.palette.grey[100] }}>
                                        <Cell width="17%">MileStone Name</Cell>
                                        <Cell width="13%">Due Date</Cell>
                                        <Cell width="12%">Status</Cell>
                                        <Cell style={{ textAlign: 'left' }}>MileStone</Cell>
                                        {user?.user_type_id !== UserTypeId.Parent && (
                                        <Cell width="15%">Action</Cell>)}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {mileStones?.length > 0 ? mileStones.map((note, index) => (
                                        <TableRow key={note.id}>
                                            <InnerCell style={{ color: 'black', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                                {note.status !== null && (
                                                    <Box
                                                        sx={{
                                                            background: '#01875F',
                                                            textAlign: 'center',
                                                            height: 25,
                                                            borderRadius: 1,
                                                            py: 0.3,
                                                            px: 1,
                                                            display: 'inline-block'
                                                        }}
                                                    >
                                                        <Typography fontSize={12} color={'white'}>{
                                                            MentorMileStoneStatus.find((a) => a.value === note.status)?.label ||
                                                            StudentMileStoneStatus.find((a) => a.value === note.status)?.label}</Typography>
                                                    </Box>
                                                )}
                                                {note.milestone_name}
                                            </InnerCell>
                                            <InnerCell>
                                                {moment(note.due_date).format('DD, MMM, YYYY')}
                                            </InnerCell>
                                            <InnerCell>
                                                <CommonSelect
                                                    value={note.status}
                                                    onChange={async (e) => {
                                                        try {
                                                            const response = await updateMileStone(note?.milestone_id, {
                                                                project_id: projectId,
                                                                "status": e.target.value
                                                            });
                                                            if (response?.success) {
                                                                showNotification(response?.message, 'success');
                                                                setOpen(false);
                                                                dispatch(setLoading({
                                                                    loading: false
                                                                }));
                                                                getMileStones();
                                                                if (getProjectDetails) {
                                                                    getProjectDetails();
                                                                }
                                                            }
                                                        } catch (err) {
                                                            const errorMessage = err.response?.data?.errors?.[0]?.message || err.response?.data?.message || err?.response?.data?.error;
                                                            showNotification(errorMessage, 'error');
                                                            dispatch(setLoading({
                                                                loading: false
                                                            }));
                                                        }
                                                    }}
                                                    placeholder={fromMentor ? "Approved" : "Testing"}
                                                    options={fromMentor ? MentorMileStoneStatus : StudentMileStoneStatus}
                                                />
                                            </InnerCell>
                                            <InnerCell style={{ textAlign: 'left' }}>
                                                {note.description}
                                            </InnerCell>
                                            {!fromMentor && user?.user_type_id === UserTypeId.Student ? (
                                                <InnerCell>
                                                    <Box display="flex" gap={1} justifyContent="center">
                                                        <CommonButton
                                                            label="Edit"
                                                            color="black"
                                                            background="white"
                                                            height="30px"
                                                            width="50px"
                                                            marginBottom="0px"
                                                            border={`1px solid #0000001A`}
                                                            handleClick={() => {
                                                                setOpen(true);
                                                                setEditData(note);
                                                                setFormData({
                                                                    name: note?.milestone_name,
                                                                    due_date: note?.due_date !== null ? moment(note?.due_date).format('YYYY-MM-DD') : '',
                                                                    status: note?.status,
                                                                    notes: note?.description
                                                                });
                                                            }}
                                                        />
                                                        <CommonButton
                                                            label="Delete"
                                                            color="white"
                                                            background="red"
                                                            height="30px"
                                                            width="70px"
                                                            marginBottom="0px"
                                                            handleClick={() => {
                                                                setEditData(note);
                                                                setDeleteOpen(true);
                                                            }}
                                                        />
                                                    </Box>
                                                </InnerCell>) : user?.user_type_id === UserTypeId.Mentor && (
                                                <InnerCell>
                                                    <Box display="flex" gap={1} justifyContent="center">
                                                        <CommonButton
                                                            label="Feedback"
                                                            color="black"
                                                            background="white"
                                                            height="25px"
                                                            width="80px"
                                                            marginBottom="0px"
                                                            border={`1px solid #0000001A`}
                                                            handleClick={() => {
                                                                setFeedBack(true);
                                                                setEditData(note);
                                                            }}
                                                        />
                                                    </Box>
                                                </InnerCell>
                                            )}
                                        </TableRow>
                                    )) : (
                                        <TableRow>
                                            <TableCell colSpan={5} align="center">
                                                <Typography>No MileStone Found</Typography>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </StyledTableContainer>
                    </Fragment>
                }
            />
        </Fragment>
    )
}

export default MileStone;